/**
 * "Check-in / Check-out" — the prompt's per-login memory.
 *
 * "Not now" must be remembered for THIS sign-in only: the next login asks
 * again ("when logged in and checked out, the first thing the popup should
 * ask is please check in first"). A login mints a new token, so the token's
 * issue time is the natural key; sessionStorage keeps it per tab.
 */
const KEY = "xale_checkin_prompt_dismissed";

function tokenIat(): string {
  try {
    const token = localStorage.getItem("xale_access_token");
    const part = token?.split(".")[1];
    if (!part) return "";
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    const iat = JSON.parse(json)?.iat;
    return iat ? String(iat) : "";
  } catch {
    return "";
  }
}

export function isCheckInPromptDismissed(): boolean {
  try {
    const iat = tokenIat();
    return !!iat && sessionStorage.getItem(KEY) === iat;
  } catch {
    return false;
  }
}

export function dismissCheckInPrompt(): void {
  try {
    const iat = tokenIat();
    if (iat) sessionStorage.setItem(KEY, iat);
  } catch {
    /* storage disabled */
  }
}

export function clearCheckInPromptDismissal(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* storage disabled */
  }
}

/** Interval between "I'm still here" heartbeats while checked in. */
export const CHECK_IN_HEARTBEAT_MS = 120_000;

export function describeAutoCheckout(
  reason: string | null | undefined,
  minutes: number | null | undefined,
): string | null {
  switch (reason) {
    case "INACTIVITY":
      return minutes
        ? `You were checked out automatically after ${minutes} minutes of inactivity.`
        : "You were checked out automatically for inactivity.";
    case "SCHEDULED":
      return "You were checked out automatically at your organisation's daily check-out time.";
    case "DEACTIVATED":
      return "You were checked out because your account was deactivated.";
    case "ADMIN":
      return "You were checked out by your administrator.";
    default:
      return null;
  }
}

/**
 * A check-out the person chose themselves must not be answered with "Please
 * check in first" a second later — it counts as "not now" for this sign-in.
 * The pill's check-out sets this; the gate consumes it on the next state
 * flip so a SERVER check-out (sweep / admin) is still told apart and re-opens
 * the prompt even when the socket push didn't reach this tab.
 */
let manualCheckOutPending = false;
export function markManualCheckOut(): void {
  manualCheckOutPending = true;
  dismissCheckInPrompt();
}
export function consumeManualCheckOut(): boolean {
  const was = manualCheckOutPending;
  manualCheckOutPending = false;
  return was;
}
